import React, { useState } from 'react';
import styled from 'styled-components';
import {
  MapPin,
  Mail,
  Clock,
  Send,
  Facebook,
  Twitter,
  Linkedin,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Globe,
  Loader2
} from 'lucide-react';
import { useContacts, SUJETS_CONTACT } from '../hooks/useContacts';
import { useSiteContent } from '../hooks/useSiteContent';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const ContactCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 0 auto 3rem;
  max-width: 1100px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  text-align: center;
  transition: var(--transition);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }

  .icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, var(--primary-blue), #004d8f);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: var(--white);
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: var(--text-medium);
    line-height: 1.6;
  }

  a {
    color: var(--primary-blue);
    font-weight: 600;

    &:hover {
      color: var(--secondary-red);
    }
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem 2rem;
  color: var(--white);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    opacity: 0.9;
    font-size: 0.95rem;
  }
`;

const FormBody = styled.form`
  padding: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;

    span {
      color: var(--secondary-red);
    }
  }

  input, select, textarea {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    transition: var(--transition);

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }

    &::placeholder {
      color: var(--text-light);
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  padding: 1rem;
  border: none;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MapCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  overflow: hidden;
  height: 300px;
  position: relative;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const SocialCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  text-align: center;

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const SocialButton = styled.a`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
  background: ${props => props.$color || 'var(--background-light)'};
  color: ${props => props.$iconColor || 'var(--text-dark)'};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const FAQSection = styled.div`
  margin-top: 3rem;
`;

const FAQTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 2rem;
  color: var(--text-dark);
  text-align: center;
  margin-bottom: 2rem;
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FAQItem = styled.div`
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  font-weight: 600;
  color: var(--text-dark);
  background: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--background-light);
  }

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
  }
`;

const FAQAnswer = styled.div`
  padding: 0 1.25rem 1.25rem;
  color: var(--text-medium);
  line-height: 1.6;
  font-size: 0.95rem;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SuccessMessage = styled.div`
  background: rgba(0, 150, 57, 0.1);
  border: 2px solid var(--accent-green);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: var(--accent-green);
    flex-shrink: 0;
  }

  p {
    color: var(--text-dark);
    font-weight: 600;
  }
`;

const ICONS = {
  MapPin,
  Mail,
  Clock,
  Facebook,
  Twitter,
  Linkedin
};

const Contact = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendMessage } = useContacts();
  const { content } = useSiteContent('contact_page');

  const contactPage = content || {};
  const cards = Array.isArray(contactPage.cards) ? contactPage.cards : [];
  const faqData = Array.isArray(contactPage.faq) ? contactPage.faq : [];
  const socialLinks = Array.isArray(contactPage.socialLinks) ? contactPage.socialLinks : [];

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    sujet: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await sendMessage(formData);

    if (result.success) {
      setIsSubmitted(true);
      setFormData({ nom: '', email: '', sujet: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 5000);
    } else {
      alert('Erreur: ' + result.error);
    }

    setIsSubmitting(false);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>{contactPage.heroTitle || 'Contactez-nous'}</HeroTitle>
          <HeroSubtitle>
            {contactPage.heroSubtitle || "Notre équipe est à votre écoute pour toute question, suggestion ou demande d'assistance"}
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <ContentWrapper>
          <ContactCards>
            {cards.map((card, index) => {
              const Icon = ICONS[card.icon] || MapPin;
              const lines = Array.isArray(card.lines) ? card.lines : [];
              const links = Array.isArray(card.links) ? card.links : [];

              return (
                <ContactCard key={`${card.title || 'card'}-${index}`}>
                  <div className="icon">
                    <Icon size={28} />
                  </div>
                  <h3>{card.title || ''}</h3>
                  <p>
                    {lines.map((line, idx) => (
                      <React.Fragment key={`line-${idx}`}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                    {links.map((link, idx) => (
                      <React.Fragment key={`link-${idx}`}>
                        <a href={link.href || '#'}>{link.label || ''}</a>
                        {idx < links.length - 1 ? <br /> : null}
                      </React.Fragment>
                    ))}
                  </p>
                </ContactCard>
              );
            })}
          </ContactCards>

          <MainGrid>
            <FormCard>
              <FormHeader>
                <h2>{contactPage.formTitle || 'Envoyez-nous un message'}</h2>
                <p>{contactPage.formSubtitle || 'Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais'}</p>
              </FormHeader>
              <FormBody onSubmit={handleSubmit}>
                {isSubmitted && (
                  <SuccessMessage>
                    <CheckCircle size={24} />
                    <p>Votre message a été envoyé avec succès ! Nous vous répondrons rapidement.</p>
                  </SuccessMessage>
                )}

                <FormGrid>
                  <FormGroup>
                    <label>Nom complet <span>*</span></label>
                    <input
                      type="text"
                      placeholder="Votre nom"
                      required
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Email <span>*</span></label>
                    <input
                      type="email"
                      placeholder="votre@email.com"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </FormGroup>
                </FormGrid>

                <FormGrid>
                  <FormGroup>
                    <label>Sujet <span>*</span></label>
                    <select
                      required
                      value={formData.sujet}
                      onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                    >
                      <option value="">Sélectionnez...</option>
                      {SUJETS_CONTACT.map(sujet => (
                        <option key={sujet.value} value={sujet.value}>{sujet.label}</option>
                      ))}
                    </select>
                  </FormGroup>
                </FormGrid>

                <FormGroup className="full-width">
                  <label>Message <span>*</span></label>
                  <textarea
                    placeholder="Votre message..."
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </FormGroup>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><Loader2 size={18} className="animate-spin" /> Envoi en cours...</>
                  ) : (
                    <><Send size={18} /> Envoyer le message</>
                  )}
                </SubmitButton>
              </FormBody>
            </FormCard>

            <InfoSection>
              <MapCard>
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <iframe
                    src="https://www.google.com/maps?q=Avenue+Colonel+NELDE+ROGOBER,+BP+898,+N%27Djamena,+Tchad&output=embed"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localisation Médiature de la République"
                  />
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Avenue+Colonel+NELDE+ROGOBER,+BP+898,+N%27Djamena,+Tchad"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      background: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      zIndex: 10
                    }}
                  >
                    Voir la carte plus grande
                  </a>
                </div>
              </MapCard>

              <SocialCard>
                <h3>{contactPage.socialTitle || 'Suivez-nous sur les réseaux sociaux'}</h3>
                <SocialLinks>
                  {socialLinks.map((social, index) => {
                    const Icon = ICONS[social.icon] || Globe;
                    return (
                      <SocialButton
                        key={`${social.icon || 'social'}-${index}`}
                        href={social.url || '#'}
                        $color={social.color || 'var(--background-light)'}
                        $iconColor="white"
                        aria-label={social.icon || 'Social'}
                      >
                        <Icon size={24} />
                      </SocialButton>
                    );
                  })}
                </SocialLinks>
              </SocialCard>
            </InfoSection>
          </MainGrid>

          <FAQSection>
            <FAQTitle>
              <HelpCircle size={28} style={{ display: 'inline', marginRight: '0.5rem', color: 'var(--accent-gold)' }} />
              {contactPage.faqTitle || 'Questions fréquentes'}
            </FAQTitle>
            <FAQGrid>
              {faqData.map((faq, index) => (
                <FAQItem key={`faq-${index}`}>
                  <FAQQuestion onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    {openFAQ === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </FAQQuestion>
                  <FAQAnswer $isOpen={openFAQ === index}>
                    {faq.answer}
                  </FAQAnswer>
                </FAQItem>
              ))}
            </FAQGrid>
          </FAQSection>
        </ContentWrapper>
      </Container>
    </PageContainer>
  );
};

export default Contact;
